import dayjs, { Dayjs } from "utils/dayjs"
import { DurationType, EMPTY_GUID, Purpose, TEMP_LMID_PREFIX } from "config/constants"
import i18n from "intl/i18n"

import { Guid, ITranslations } from "@/types"
import { ValuationStatus } from "models/Valuation"
import { InvoiceAssignmentStatus } from "models/Invoice"
import { ConstructionMonth, LocalCalculationRule, StartDepreciationUsing } from "models/FinancialFactors"
import { CostCategoryType } from "models/CostCategory"
import { TextType } from "components/Text/Text"
import { EvaluationParameterFinanceCategory, EvaluationParameterIFRSDepreciationStart } from "models/EvaluationParameter"
import { AssetDisposalReason, AssetOwnershipType, AssetReferenceNumberType } from "models/Asset"
import { InvoiceCategoryType } from "models/AssetInvoiceIncoming"
import { AssetStoredStatus } from 'models/AssetDisplayStatus';
import { ShippingOrderType } from "models/ShippingOrder"
import { StatusLightType } from 'components/StatusLight/StatusLight';
import { SupplierType } from 'models/Supplier';
import {
    AcquisitionCostsCalculationAdjustmentCategory,
    CalculationAdjustmentType,
    RevenueCalculationAdjustmentCategory,
    SdcCalculationAdjustmentCategory
} from "models/CalculationAdjustment"
import ReservationCartInvoicePosition from 'models/ReservationCartInvoicePosition';
import { Role } from "models/UserRoles"

export const isGuidEmpty = (guid: string): boolean => {
    return !guid || guid === EMPTY_GUID
}

export function isValidPurpose(purpose: string): boolean {
    return Object.keys(Purpose).includes(purpose)
}

export const getPurposeLabel = (purpose: Purpose): string => {
    return {
        [Purpose.rent]: i18n.t('purposes.rent'),
        [Purpose.demo]: i18n.t('purposes.demo'),
        [Purpose.sale]: i18n.t('purposes.sale'),
        [Purpose.internalUse]: i18n.t('purposes.internalUse'),
        [Purpose.bridge]: i18n.t('purposes.bridge'),
        [Purpose.relocation]: i18n.t('purposes.relocation'),
    }[purpose] || purpose
}

export const getAssetStoredStatusLabel = (status: AssetStoredStatus): string => {
    return {
        [AssetStoredStatus.notApplicable]: i18n.t('common.na'),
        [AssetStoredStatus.stored]: i18n.t('asset.stateOfStorage.stored'),
        [AssetStoredStatus.released]: i18n.t('asset.stateOfStorage.released'),
    }[status] || status
}

export const getAssetOwnershipTypeLabel = (type: AssetOwnershipType): string => {
    return {
        [AssetOwnershipType.borrowed]: i18n.t('asset.borrowed'),
        [AssetOwnershipType.purchased]: i18n.t('asset.purchased')
    }[type] || type
}

export const getCalculationAdjustmentTypeLabel = (type: CalculationAdjustmentType): string => {
    return {
        [CalculationAdjustmentType.revenue]: i18n.t('calculationAdjustments.revenues'),
        [CalculationAdjustmentType.sdc]: i18n.t('calculationAdjustments.sdc'),
        [CalculationAdjustmentType.acquisitionCosts]: i18n.t('calculationAdjustments.acquisitionCosts'),
    }[type] || type
}
export const getCalculationAdjustmentTypeSubtotalLabel = (type: CalculationAdjustmentType): string => {
    return {
        [CalculationAdjustmentType.revenue]: i18n.t('calculationAdjustments.subTotalRevenues'),
        [CalculationAdjustmentType.sdc]: i18n.t('calculationAdjustments.subTotalSdc'),
        [CalculationAdjustmentType.acquisitionCosts]: i18n.t('calculationAdjustments.subTotalAcquisitionCosts'),
    }[type] || type
}

export const getCalculationAdjustmentCategoryLabel = (category: RevenueCalculationAdjustmentCategory | SdcCalculationAdjustmentCategory | AcquisitionCostsCalculationAdjustmentCategory): string => {
    const map: Record<string, string> = {
        [RevenueCalculationAdjustmentCategory.underestimatedRepurchasedMachines]: i18n.t('calculationAdjustments.underestimatedRepurchasedMachines'),
        [SdcCalculationAdjustmentCategory.freight]: i18n.t('calculationAdjustments.freight'),
        [SdcCalculationAdjustmentCategory.mounting]: i18n.t('calculationAdjustments.mounting'),
        [SdcCalculationAdjustmentCategory.cashDiscounts]: i18n.t('calculationAdjustments.cashDiscounts'),
        [SdcCalculationAdjustmentCategory.purchaseFunding]: i18n.t('calculationAdjustments.purchaseFunding'),
        [SdcCalculationAdjustmentCategory.overestimatedRepurchasedMachines]: i18n.t('calculationAdjustments.overestimatedRepurchasedMachines'),
        [SdcCalculationAdjustmentCategory.provision]: i18n.t('calculationAdjustments.provision'),
        [SdcCalculationAdjustmentCategory.maintenance]: i18n.t('calculationAdjustments.maintenance'),
        [AcquisitionCostsCalculationAdjustmentCategory.additionalAcquisition]: i18n.t('calculationAdjustments.additionalAcquisition'),
        [AcquisitionCostsCalculationAdjustmentCategory.purchasingCostsFreight]: i18n.t('calculationAdjustments.purchasingCostsFreight'),
    }
    // All enum variants named "other" share same translation key
    map[RevenueCalculationAdjustmentCategory.other] = i18n.t('calculationAdjustments.other')
    map[SdcCalculationAdjustmentCategory.other] = i18n.t('calculationAdjustments.other')
    map[AcquisitionCostsCalculationAdjustmentCategory.other] = i18n.t('calculationAdjustments.other')
    return map[category] || category
}

export const getAssetDisposalReasonLabel = (reason: AssetDisposalReason): string => {
    return {
        [AssetDisposalReason.scrap]: i18n.t('assetDisposal.reasons.scrap'),
        [AssetDisposalReason.stolen]: i18n.t('assetDisposal.reasons.stolen'),
        [AssetDisposalReason.lost]: i18n.t('assetDisposal.reasons.lost'),
        [AssetDisposalReason.inventoryAdjustment]: i18n.t('assetDisposal.reasons.inventoryAdjustment'),
        [AssetDisposalReason.sold]: i18n.t('assetDisposal.reasons.sold'),
        [AssetDisposalReason.canceled]: i18n.t('assetDisposal.reasons.canceled'),
        [AssetDisposalReason.endOfHoldership]: i18n.t('assetDisposal.reasons.endOfHoldership'),
        [AssetDisposalReason.transit]: i18n.t('assetDisposal.reasons.transit'),
        [AssetDisposalReason.transfer]: i18n.t('assetDisposal.reasons.transfer'),
        [AssetDisposalReason.reversal]: i18n.t('assetDisposal.reasons.reversal'),
    }[reason] || reason
}

export const getShippingOrderTypeLabel = (type: ShippingOrderType): string => {
    return {
        [ShippingOrderType.delivery]: i18n.t('shippingOrderType.delivery'),
        [ShippingOrderType.return]: i18n.t('shippingOrderType.return'),
    }[type] || type
}

export const getValuationStatusLabel = (valuationStatus: ValuationStatus): string => {
    return {
        [ValuationStatus.active]: i18n.t('valuation.status.active'),
        [ValuationStatus.missingParameters]: i18n.t('valuation.status.missingParameters'),
        [ValuationStatus.noInvoiceBooked]: i18n.t('valuation.status.noInvoiceBooked'),
        [ValuationStatus.disposal]: i18n.t('valuation.status.disposal'),
    }[valuationStatus.toLowerCase()] || valuationStatus
}

export const getInvoiceAssignmentStatusLabel = (assignmentStatus: InvoiceAssignmentStatus): string => {
    return {
        [InvoiceAssignmentStatus.assigned]: i18n.t('invoices.assigned'),
        [InvoiceAssignmentStatus.assignedAutomatically]: i18n.t('invoices.assignedAutomatically'),
        [InvoiceAssignmentStatus.notAssigned]: i18n.t('invoices.notAssigned'),
        [InvoiceAssignmentStatus.notRelevant]: i18n.t('invoices.notRelevant')
    }[assignmentStatus] || assignmentStatus
}

export const getInvoiceAssignmentStatusLightType = (assignmentStatus: InvoiceAssignmentStatus): StatusLightType => {
    return {
        [InvoiceAssignmentStatus.assigned]: StatusLightType.success,
        [InvoiceAssignmentStatus.assignedAutomatically]: StatusLightType.successAlt,
        [InvoiceAssignmentStatus.notAssigned]: StatusLightType.warning,
    }[assignmentStatus] || assignmentStatus
}


export const getLabeledStatusLabel = (isLabeled: boolean): string => {
    if (isLabeled) {
        return i18n.t('filter.label.labeled')
    }
    return i18n.t('filter.label.unlabeled')
}

export const getLocalCalculationRuleLabel = (localCalculationRule: LocalCalculationRule): string => {
    return {
        [LocalCalculationRule.halfYear]: i18n.t('localCalculationRule.halfYear'),
        [LocalCalculationRule.acquisitionMonth]: i18n.t('localCalculationRule.acquisitionMonth'),
    }[localCalculationRule] || localCalculationRule
}

export const getConstructionMonth = (constructionMonth: ConstructionMonth): string => {
    return {
        [ConstructionMonth.june]: i18n.t('tenantSettings.constructionMonth.june'),
        [ConstructionMonth.july]: i18n.t('tenantSettings.constructionMonth.july')
    }[constructionMonth] || constructionMonth
}

export const getStartDepreciationUsingLabel = (startDepreciationUsing: StartDepreciationUsing): string => {
    return {
        [StartDepreciationUsing.bookingPeriod]: i18n.t('startDepreciationUsing.bookingPeriod'),
        [StartDepreciationUsing.invoiceDate]: i18n.t('startDepreciationUsing.invoiceDate'),
    }[startDepreciationUsing] || startDepreciationUsing
}

export const getCostCategoryTypeShortLabel = (costCategoryType: CostCategoryType): string => {
    return {
        [CostCategoryType.investment]: i18n.t('costCategoryType.investment.short'),
        [CostCategoryType.sdc]: i18n.t('costCategoryType.sdc.short'),
    }[costCategoryType] || costCategoryType
}

export const getInvoiceCategoryTypeShortLabel = (invoiceCategoryType: InvoiceCategoryType): string => {
    return {
        [InvoiceCategoryType.investment]: i18n.t('invoiceCategoryType.investment.short'),
        [InvoiceCategoryType.sdc]: i18n.t('invoiceCategoryType.sdc.short'),
        [InvoiceCategoryType.invNotEvaluated]: i18n.t('invoiceCategoryType.invNotEvaluated.short'),
    }[invoiceCategoryType] || invoiceCategoryType
}

export const getEvaluationParameterFinanceCategoryLabel = (evaluationParameterFinanceCategory: EvaluationParameterFinanceCategory): string => {
    return {
        [EvaluationParameterFinanceCategory.investment]: i18n.t('evaluationParameterFinanceCategory.investment'),
        [EvaluationParameterFinanceCategory.stock]: i18n.t('evaluationParameterFinanceCategory.stock'),
    }[evaluationParameterFinanceCategory] || evaluationParameterFinanceCategory
}

export const getEvaluationParameterIFRSDepreciationStartLabel = (evaluationParameterIFRSDepreciationStart: EvaluationParameterIFRSDepreciationStart): string => {
    return {
        [EvaluationParameterIFRSDepreciationStart.firstMonth]: i18n.t('evaluationParameterIFRSDepreciationStart.firstMonth'),
        [EvaluationParameterIFRSDepreciationStart.seventhMonth]: i18n.t('evaluationParameterIFRSDepreciationStart.seventhMonth'),
        [EvaluationParameterIFRSDepreciationStart.never]: i18n.t('evaluationParameterIFRSDepreciationStart.never'),
    }[evaluationParameterIFRSDepreciationStart] || evaluationParameterIFRSDepreciationStart
}

export const getRandomNumber = (): number => {
    return crypto.getRandomValues(new Uint32Array(1))[0]
}

export const formatNumber = (num: number, minimumFractionDigits: number = 0, maximumFractionDigits?: number, lng: string = i18n.language || "en") => {
    const locale = getNumberFormattingLocale(lng)
    return num.toLocaleString(locale, { minimumFractionDigits, maximumFractionDigits: typeof maximumFractionDigits === "number" ? maximumFractionDigits : 20 }) // 20 is maximum
}

export const getDecimalSeparator = (lng: string = i18n.language) => {
    const locale = getNumberFormattingLocale(lng)
    return (1.5).toLocaleString(locale)[1]
}

const getNumberFormattingLocale = (lng: string): string => {
    // for swiss we need to use de-CH locale to get the correct number formatting
    if (lng === "de" && navigator.language.startsWith("de-CH")) {
        return "de-CH"
    }
    return lng
}

export const localizeDecimalSeparator = (val: string | number) => {
    const stringValue: string = typeof val === "number" ? val.toString() : val
    const decimalSeparator = getDecimalSeparator()
    const wrongDecimalSeparator = decimalSeparator === "." ? "," : "."
    return stringValue.replace(wrongDecimalSeparator, decimalSeparator)
}

const normalizeDecimalSeparator = (val: string | number) => {
    const stringValue: string = typeof val === "number" ? val.toString() : val
    return stringValue.replace(getDecimalSeparator(), ".")
}

export const stringToNumber = (val: string): number | null => {
    if (val === "") {
        return null
    }
    const floatValue = Number(normalizeDecimalSeparator(val))
    if (isNaN(floatValue)) {
        return null
    }
    return floatValue
}

export const getDurationLabel = (count: number, type: DurationType) => {
    switch (type) {
        case DurationType.days:
            return i18n.t('common.xDays', { count })
        case DurationType.weeks:
            return i18n.t('common.xWeeks', { count })
        case DurationType.months:
            return i18n.t('common.xMonths', { count })
        case DurationType.years:
            return i18n.t('common.xYears', { count })
        default:
            return ""
    }
}

export const getDateStringFromMomentDate = (momentDate: Dayjs) => {
    return `${momentDate.format("YYYY")}-${momentDate.format("MM")}-${momentDate.format("DD")}`
}

export const toDateTimeUtc = (date: Dayjs) => {
    return date.clone().utc().toISOString()
}

export const toDateWithoutTimezone = (date: Dayjs) => {
    return date.clone().utcOffset(0, true).format("YYYY-MM-DD")
}

export const toDateTimeWithoutTimezone = (date: Dayjs) => {
    return date.clone().utcOffset(0, true).format()
}

// Relabels a fake-UTC wall-clock value (offset 0, as produced by a DateTimeControl picker)
// with the real UTC offset that applies to THIS date/time - not simply "now"'s offset
// (EMTMP-1051). In a DST-observing zone the offset differs between summer and winter, so
// using dayjs().utcOffset() (the offset in effect right now, when the code happens to run)
// mislabels any date on the other side of a DST transition relative to today - e.g.
// picking a November date while the browser's "now" is still in summer time stamps it
// with the summer offset instead of the winter one. Instead, build a native Date from the
// same Y/M/D/H/M/S digits (interpreted in the browser's local timezone) and read its own
// getTimezoneOffset() - the runtime already resolves that correctly per-date via the OS's
// timezone database, DST or not, so no separate DST-country check is needed here.
export const toDateTimeWithOffset = (date: Dayjs) => {
    const offsetMinutes = -new Date(
        date.year(), date.month(), date.date(),
        date.hour(), date.minute(), date.second(), date.millisecond(),
    ).getTimezoneOffset()

    return date.clone().utcOffset(offsetMinutes, true).format()
}

export const toDayjsWithoutTimezone = (dateTime: string) => {
    const withoutOffset = dateTime.replace(/(Z|[+-]\d{2}:?\d{2})$/, "")
    return dayjs.utc(withoutOffset)
}

export const nowWithoutTimezone = () => dayjs().utcOffset(0, true)

export const toDateTimeKeepingOffset = (date: Dayjs, original?: string | null) => {
    if (original && toDayjsWithoutTimezone(original).isSame(date)) {
        return original
    }

    return toDateTimeWithOffset(date)
}

export const printPeriod = ({
    start,
    end,
    withTime
}: {
    start?: Dayjs | null
    end?: Dayjs | null
    withTime?: boolean

}) => {
    const parts: string[] = []
    if (start) {
        parts.push(start.format(withTime ? i18n.t("config.dateLongTimeFormat") : i18n.t("config.dateLongFormat")))
    }
    if (end) {
        parts.push(end.format(withTime ? i18n.t("config.dateLongTimeFormat") : i18n.t("config.dateLongFormat")))
    }
    return parts.join(" – ")
}

export const countDecimals = (decimal: number | string) => {
    const num = typeof decimal === "string" ? stringToNumber(decimal) : decimal;

    if (Number.isInteger(num) || num === null) {
        return 0;
    }

    const text = num.toString();

    if (text.indexOf('e-') > -1) {
        const trail = text.split('e-')[1];
        const deg = parseInt(trail, 10);
        return deg;
    } else {
        const index = text.indexOf(".");
        return text.length - index - 1; // Otherwise use simple string function to count
    }
}

export const getArticleDescriptionHeaderTextType = (description: string): TextType => {
    if (description.length <= 40) return "h2"
    if (description.length <= 50) return "h3"
    if (description.length <= 60) return "h4"
    if (description.length <= 80) return "h5"
    return "h6"
}

export function downloadUrlAsFile(url: string) {
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", "true")
    link.setAttribute("target", "_blank")
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

export function downloadBlobAsFile(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

export function getFileNameAndExtension(fileName: string) {
    const parts = fileName.split(".")
    const extension = parts.length > 1 ? parts.pop() : ""
    const name = parts.join(".")
    return {
        name,
        extension,
    }
}

export function arrayToTranslations(arr: string[], language: string) {
    return arr.map(feature => ({ [language]: feature }))
}

export function translationsToArray(translations: ITranslations[], language: string) {
    return translations.map(translation => translation[language])
}

export function stringToTranslations(feature: string, language: string): ITranslations {
    return {
        [language]: feature
    }
}

export function isValid6CharHexColor(color: string) {
    if (!color) return
    return /^#([0-9A-F]{6})$/i.test(color)
}

export function applyLineBreakAfterRule(after: number, text: string) {
    const lineBreakRegExp = new RegExp(`(.{1,${after}})(?:\n|$| )`, "g")
    return text.replace(lineBreakRegExp, "$1\n").trimEnd()
}

export function getSupplierTypeLabel(supplierType: SupplierType) {
    return {
        [SupplierType.thirdParty]: i18n.t('invoices.supplier.type.thirdParty'),
        [SupplierType.affiliatedCompany]: i18n.t('invoices.supplier.type.affiliatedCompany'),
    }[supplierType] || ""
}

export function getFuelLabel(fuel: number) {
    switch (fuel) {
        case 0:
            return i18n.t('common.empty')
        case 25:
            return '1/4'
        case 50:
            return '1/2'
        case 75:
            return '3/4'
        case 100:
            return i18n.t('common.full')
        default:
            return `${fuel} %`
    }
}

export function findCreatedInvoicePositionId(prevInvoicePositions: ReservationCartInvoicePosition[],
    currentInvoicePositions: ReservationCartInvoicePosition[]): Guid {
    const prevIPKeyVal = prevInvoicePositions.reduce<Record<Guid, boolean>>((accum, iP) => ({ ...accum, [iP.id as string]: true }), {})
    const createdIP = currentInvoicePositions.find((iP) => !prevIPKeyVal[iP.id as string])
    return createdIP?.id || ""
}

export function getRoleLabel(role: Role) {
    return {
        [Role.SuperAdmin]: i18n.t('rolesAndPermissions.roleValues.SuperAdmin'),
        [Role.TenantAdmin]: i18n.t('rolesAndPermissions.roleValues.TenantAdmin'),
        [Role.StockCoordinator]: i18n.t('rolesAndPermissions.roleValues.StockCoordinator'),
        [Role.ShippingCoordinator]: i18n.t('rolesAndPermissions.roleValues.ShippingCoordinator'),
        [Role.SalesAdmin]: i18n.t('rolesAndPermissions.roleValues.SalesAdmin'),
        [Role.Controller]: i18n.t('rolesAndPermissions.roleValues.Controller'),
    }[role] || role
}

export const getAssetReferenceNumberSaleTypeLabel = (type: AssetReferenceNumberType): string => {
    return {
        [AssetReferenceNumberType.Initial]: i18n.t('asset.referenceNumberType.initial'),
        [AssetReferenceNumberType.Current]: i18n.t('asset.referenceNumberType.current'),
    }[type] || type
}

export const isTempLmid = (lmid: string) => {
    return lmid.startsWith(TEMP_LMID_PREFIX)
}
